/**
 * shadcn/ui Table — vendored + tokenized for CertAIn v2 (Phase 1 fan-out foundation).
 *
 * Semantic HTML table subcomponents. Stock shadcn `bg-muted`/`text-muted-foreground`/
 * `border` swapped for CertAIn semantic utilities (bg-surface-subtle,
 * text-text-secondary, border-border-default) so they resolve to the v2
 * light/navy values inside AppShellV2's `[data-theme="v2"]` scope. Guard-clean
 * (no hex / rgb / arbitrary color).
 *
 * Density: matches design/v2-direction/index.html `table.tbl` — thead th 12px
 * muted on a subtle-tinted band, tbody td 13.5px with hairline row borders and a
 * subtle hover. This is the shared surface behind BOTH the All Candidates table
 * view and the Actions Log table (patterns/DataTableV2 composes it).
 */
import * as React from "react";
import { cn } from "@/lib/utils";

const Table = React.forwardRef<
  HTMLTableElement,
  React.HTMLAttributes<HTMLTableElement>
>(({ className, ...props }, ref) => (
  <div className="relative w-full overflow-x-auto">
    <table
      ref={ref}
      className={cn(
        "w-full border-collapse text-[13.5px] text-text-primary",
        className,
      )}
      {...props}
    />
  </div>
));
Table.displayName = "Table";

const TableHeader = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <thead ref={ref} className={cn("[&_tr]:border-b", className)} {...props} />
));
TableHeader.displayName = "TableHeader";

const TableBody = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tbody
    ref={ref}
    className={cn("[&_tr:last-child]:border-0", className)}
    {...props}
  />
));
TableBody.displayName = "TableBody";

const TableFooter = React.forwardRef<
  HTMLTableSectionElement,
  React.HTMLAttributes<HTMLTableSectionElement>
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={cn(
      "border-t border-border-default bg-surface-subtle/50 font-medium",
      className,
    )}
    {...props}
  />
));
TableFooter.displayName = "TableFooter";

const TableRow = React.forwardRef<
  HTMLTableRowElement,
  React.HTMLAttributes<HTMLTableRowElement>
>(({ className, ...props }, ref) => (
  <tr
    ref={ref}
    className={cn(
      "border-b border-border-default transition-colors data-[state=selected]:bg-surface-subtle",
      className,
    )}
    {...props}
  />
));
TableRow.displayName = "TableRow";

const TableHead = React.forwardRef<
  HTMLTableCellElement,
  React.ThHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <th
    ref={ref}
    className={cn(
      // Canonical table.tbl thead th: 12px, 500, muted, subtle-tinted band.
      "whitespace-nowrap bg-surface-subtle/50 px-4 py-2.5 text-left align-middle text-[12px] font-medium tracking-[0.01em] text-text-secondary [&:has([role=checkbox])]:pr-0",
      className,
    )}
    {...props}
  />
));
TableHead.displayName = "TableHead";

const TableCell = React.forwardRef<
  HTMLTableCellElement,
  React.TdHTMLAttributes<HTMLTableCellElement>
>(({ className, ...props }, ref) => (
  <td
    ref={ref}
    // Canonical table.tbl tbody td: padding 11px 16px, middle-aligned.
    className={cn(
      "px-4 py-[11px] align-middle [&:has([role=checkbox])]:pr-0",
      className,
    )}
    {...props}
  />
));
TableCell.displayName = "TableCell";

const TableCaption = React.forwardRef<
  HTMLTableCaptionElement,
  React.HTMLAttributes<HTMLTableCaptionElement>
>(({ className, ...props }, ref) => (
  <caption
    ref={ref}
    className={cn("mt-4 text-sm text-text-secondary", className)}
    {...props}
  />
));
TableCaption.displayName = "TableCaption";

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
};
